import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Polyline, Circle, Polygon } from "@react-google-maps/api";
import {
  pathIntersectsViewportBounds,
  getLinkStartNodeId,
  getLinkEndNodeId,
} from "../../../helpers/modelBuilder/mbChatPureUtils";
import { bindingLayerKey } from "../../../helpers/modelBuilder/entityBinding";
import { showLinkTypeAtZoom } from "./visibilityHelpers/linkVisibility";
import { linkEndpointIds, selectNodesForRender, MAX_RENDERED_NODES, MIN_DETAIL_RENDER_ZOOM } from "./visibilityHelpers/nodeVisibility";
import { selectParcelsForRender } from "./visibilityHelpers/parcelVisibility";
import { sampleRows } from "./visibilityHelpers/sampleRows";

const containerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "8px",
  overflow: "hidden",
  border: "1px solid #2a2a2a",
  background: "#2c2c2c",
};

const mapButtonStyle = {
  padding: "6px 10px",
  fontSize: 14,
  borderRadius: 6,
  cursor: "pointer",
  color: "#fff",
  border: "1px solid #1f2937",
};

const MAX_RENDERED_PARCELS = 1200;
const PARCEL_FIT_SAMPLE_SIZE = 3000;
const VIEWPORT_IDLE_DEBOUNCE_MS = 200;

const TemporalLink = React.memo(function TemporalLink({ link, active, color, onSelect }) {
  return <Polyline
    path={link.path}
    onClick={(event) => { event?.domEvent?.stopPropagation?.(); onSelect?.("link", link.id); }}
    options={{
      strokeColor: active ? color || "#22d3ee" : "#64748b",
      strokeOpacity: active ? 1 : 0.6,
      strokeWeight: active ? 10 : 5,
      clickable: true,
      zIndex: active ? 42 : 35,
      icons: active && window.google?.maps ? [{
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          fillColor: "#ffffff",
          fillOpacity: 1,
          strokeColor: "#0f172a",
          strokeWeight: 1,
          scale: 4,
        },
        offset: "50%",
      }] : [],
    }}
  />;
});

const TemporalNode = React.memo(function TemporalNode({ node, active, color, onSelect }) {
  return <Circle
    center={{ lat: node.lat, lng: node.lng }}
    radius={active ? 24 : 11}
    onClick={(event) => { event?.domEvent?.stopPropagation?.(); onSelect?.("node", node.id); }}
    options={{
      fillColor: active ? color || "#fbbf24" : "#94a3b8",
      fillOpacity: 0.95,
      strokeColor: "#0f172a",
      strokeWeight: 2,
      clickable: true,
      zIndex: active ? 44 : 36,
    }}
  />;
});

export default function MBMapComponent({
  isVisible = true,
  // maps loader state comes from Chat (ONLY Chat calls useJsApiLoader)
  isLoaded,
  loadError,

  // data to draw (comes from Chat)
  links = [],
  overlayLinks = [],
  nodes = [],
  overlayNodes = [],
  nodeLinkAssociation = false,
  overlayPolygons = [],
  hazardPolygons = [],
  selectedHazardFeatureIndex = "",
  parcels = [],
  polygons = [],

  defaultCenter,
  defaultZoom,
  onViewportChange,
  searchValue = "",
  onChangeSearchValue,
  onSubmitSearch,

  // polygon state (owned by Chat)
  isSelectingPolygon = false,
  draftPolygon = [],
  savedPolygon = null,

  // polygon actions (implemented in Chat)
  onStartPolygon,
  onCancelPolygon,
  onAddPolygonPoint,
  onUndoPolygonPoint,
  onSavePolygon,

  selectedNodeIds = new Set(),
  selectedLinkIds = new Set(),
  selectedParcelIds = new Set(),
  selectedPolygonIds = new Set(),
  selectedPolygonColor = "#facc15",
  activeLayer = "nodes",
  activeLayerId = "",
  onToggleLink,
  onToggleNode,
  onToggleParcel,
  onTogglePolygon,
  onSelectHazardFeature,
  focusTarget = null,
  fitSelection = null,
  isDrawingDraftLink = false,
  draftLinkPath = [],
  onAddDraftLinkPoint,
  draftLinkFocus = null,
  draftLinkCandidateArrows = [],
  contraflowMode = false,
  blockedRoadMode = false,
  analysisOverlay = null,
  temporalOverlay = null,
  temporalHazardAvailable = false,
  temporalHazardEnabled = false,
  temporalHazardPolygons = [],
  onTemporalHazardChange,
  onTemporalSelect,
  returnToSimulationResults = false,
  onReturnToSimulationResults,
}) {
  const mapRef = useRef(null);
  const idleTimerRef = useRef(null);
  const didInitialFitRef = useRef(false);
  const handledFocusTargetRef = useRef(null);
  const fittedTemporalIdRef = useRef("");
  const [mapReady, setMapReady] = useState(false);
  const [viewportBounds, setViewportBounds] = useState(null);
  const [mapZoom, setMapZoom] = useState(Number.isFinite(Number(defaultZoom)) ? Number(defaultZoom) : 12);
  const [showAllLinks, setShowAllLinks] = useState(false);
  const hasDisplayLinks = activeLayer === "links" || overlayLinks.length > 0;
  const canRenderDetailLayer = !Number.isFinite(mapZoom) || mapZoom >= MIN_DETAIL_RENDER_ZOOM;
  const selectedBlockedBothDirections = useMemo(() => {
    if (!blockedRoadMode) return new Set();
    const selected = links.filter((link) => selectedLinkIds.has(String(link?.link_id)));
    const hasEndpoints = (link) => [getLinkStartNodeId(link), getLinkEndNodeId(link)].every((value) => value !== null && value !== undefined && value !== "");
    const directions = new Set(selected.filter(hasEndpoints).map((link) => `${getLinkStartNodeId(link)}:${getLinkEndNodeId(link)}`));
    return new Set(selected.filter((link) => hasEndpoints(link) && directions.has(`${getLinkEndNodeId(link)}:${getLinkStartNodeId(link)}`)).map((link) => String(link.link_id)));
  }, [blockedRoadMode, links, selectedLinkIds]);
  const selectedNodeHaloRadius = useMemo(() => {
    const zoom = Number.isFinite(mapZoom) ? mapZoom : MIN_DETAIL_RENDER_ZOOM;
    // Google Maps circle radii use metres. Doubling for every zoom level out
    // keeps the halo prominent even as the map scale gets wider.
    return Math.min(600, Math.max(48, 48 * (2 ** (MIN_DETAIL_RENDER_ZOOM - zoom))));
  }, [mapZoom]);
  const layerRenderKey = `${String(activeLayer || "")}:${String(activeLayerId || "")}`;
  const analysisOverlayActive = analysisOverlay
    && String(analysisOverlay.binding?.type || "") === String(activeLayer || "")
    && bindingLayerKey(analysisOverlay.binding) === String(activeLayerId || "");
  const analysisColorFor = useCallback((id) => {
    if (!analysisOverlayActive) return "";
    const value = Number(analysisOverlay?.values?.[String(id)]);
    if (!Number.isFinite(value)) return "";
    return analysisOverlay.ranges?.find((range, index, ranges) =>
      value >= Number(range.min) && (index === ranges.length - 1 || value <= Number(range.max))
    )?.color || "";
  }, [analysisOverlay, analysisOverlayActive]);
  const allNodesForRender = useMemo(
    () => (activeLayer === "nodes" ? nodes : Array.isArray(overlayNodes) ? overlayNodes : []),
    [activeLayer, nodes, overlayNodes]
  );
  const linksForRender = useMemo(() => {
    if (activeLayer !== "links") return [];
    if (!viewportBounds) return [];
    const all = Array.isArray(links) ? links : [];
    const visible = all.filter((link) => {
      const id = String(link?.link_id ?? "");
      const isSelected = selectedLinkIds?.has?.(id);
      const isFocused = focusTarget?.layer === "links" && String(focusTarget.id) === id;
      // Keep selected links mounted at every zoom, even before bounds catch up with a map move.
      if (isSelected) return true;
      return (showAllLinks || showLinkTypeAtZoom(link?.type, mapZoom) || isFocused || !!analysisColorFor(id))
        && pathIntersectsViewportBounds(link?.path, viewportBounds);
    }).sort((a, b) => Number(!!selectedLinkIds?.has?.(String(a?.link_id))) - Number(!!selectedLinkIds?.has?.(String(b?.link_id))));
    return visible;
  }, [activeLayer, analysisColorFor, focusTarget, links, mapZoom, selectedLinkIds, showAllLinks, viewportBounds]);
  const overlayLinksForRender = useMemo(() => {
    if (!viewportBounds) return [];
    const all = Array.isArray(overlayLinks) ? overlayLinks : [];
    return all.filter((link) =>
      (showAllLinks || showLinkTypeAtZoom(link?.type, mapZoom))
      && pathIntersectsViewportBounds(link?.path, viewportBounds)
    );
  }, [mapZoom, overlayLinks, showAllLinks, viewportBounds]);
  const allAssociatedNodeIds = useMemo(
    () => nodeLinkAssociation ? linkEndpointIds(overlayLinks) : new Set(),
    [nodeLinkAssociation, overlayLinks]
  );
  const visibleAssociatedNodeIds = useMemo(
    () => nodeLinkAssociation ? linkEndpointIds(overlayLinksForRender) : new Set(),
    [nodeLinkAssociation, overlayLinksForRender]
  );
  const nodesForRender = useMemo(
    () => selectNodesForRender({
      nodes: allNodesForRender,
      activeLayer,
      viewportBounds,
      selectedNodeIds,
      analysisColorFor,
      analysisOverlayActive,
      canRenderDetailLayer,
      nodeLinkAssociation,
      allEndpointIds: allAssociatedNodeIds,
      visibleEndpointIds: visibleAssociatedNodeIds,
      maxRenderedNodes: MAX_RENDERED_NODES,
    }),
    [activeLayer, allNodesForRender, analysisColorFor, analysisOverlayActive, allAssociatedNodeIds, canRenderDetailLayer, nodeLinkAssociation, viewportBounds, selectedNodeIds, visibleAssociatedNodeIds]
  );
  const showNodesOverlay = activeLayer !== "nodes" && nodesForRender.length > 0;
  const polygonsForRender = useMemo(
    () => (activeLayer === "polygon" ? polygons : Array.isArray(overlayPolygons) ? overlayPolygons : []),
    [activeLayer, polygons, overlayPolygons]
  );
  const showPolygonOverlay = activeLayer !== "polygon" && polygonsForRender.length > 0;
  const activeTemporalLinks = new Map((temporalOverlay?.activeLinks || []).map((item) => [item.id, item]));
  const activeTemporalNodes = new Map((temporalOverlay?.activeNodes || []).map((item) => [item.id, item.color]));

  const updateViewportBounds = useCallback(() => {
    const map = mapRef.current;
    const bounds = map?.getBounds?.();
    if (!bounds) return;
    const ne = bounds.getNorthEast?.();
    const sw = bounds.getSouthWest?.();
    if (!ne || !sw) return;

    const next = {
      north: Number(ne.lat()),
      south: Number(sw.lat()),
      east: Number(ne.lng()),
      west: Number(sw.lng()),
    };

    setViewportBounds((prev) => {
      if (
        prev &&
        Math.abs(prev.north - next.north) < 1e-9 &&
        Math.abs(prev.south - next.south) < 1e-9 &&
        Math.abs(prev.east - next.east) < 1e-9 &&
        Math.abs(prev.west - next.west) < 1e-9
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const parcelsForRender = useMemo(() => {
    if (activeLayer !== "parcels") return [];
    return selectParcelsForRender({
      parcels,
      viewportBounds,
      selectedParcelIds,
      analysisColorFor,
      canRenderDetailLayer,
      maxRenderedParcels: MAX_RENDERED_PARCELS,
    });
  }, [activeLayer, analysisColorFor, canRenderDetailLayer, parcels, viewportBounds, selectedParcelIds]);

  const parcelsForFit = useMemo(() => {
    if (activeLayer !== "parcels") return [];
    const all = Array.isArray(parcels) ? parcels : [];
    return sampleRows(all, PARCEL_FIT_SAMPLE_SIZE);
  }, [activeLayer, parcels]);

  const polygonToRender = isSelectingPolygon ? draftPolygon : savedPolygon;

  // Map click adds points ONLY while selecting
  const onMapClick = useCallback(
    (e) => {
      if (isDrawingDraftLink) {
        const lat = e?.latLng?.lat?.();
        const lng = e?.latLng?.lng?.();
        if (lat == null || lng == null) return;
        onAddDraftLinkPoint?.({ lat, lng });
        return;
      }
      if (!isSelectingPolygon) return;
      const lat = e?.latLng?.lat?.();
      const lng = e?.latLng?.lng?.();
      if (lat == null || lng == null) return;
      onAddPolygonPoint?.({ lat, lng });
    },
    [isDrawingDraftLink, onAddDraftLinkPoint, isSelectingPolygon, onAddPolygonPoint]
  );

  // Optional: fit bounds to data once loaded (helps avoid “nothing visible”)
  const fitToData = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return false;

    const bounds = new window.google.maps.LatLngBounds();
    let added = 0;

    // The network is the most reliable extent for a fresh builder map.
    const visibleLinks = links;
    const visibleParcels = activeLayer === "parcels" ? parcelsForFit : [];
    for (const l of visibleLinks) {
      if (!l?.path?.length) continue;
      for (const p of l.path) {
        bounds.extend(p);
        added++;
      }
    }
    for (const n of allNodesForRender) {
      if (typeof n.lat !== "number" || typeof n.lng !== "number") continue;
      bounds.extend({ lat: n.lat, lng: n.lng });
      added++;
    }
    for (const p of visibleParcels) {
      if (typeof p.lat !== "number" || typeof p.lng !== "number") continue;
      bounds.extend({ lat: p.lat, lng: p.lng });
      added++;
    }
    for (const poly of polygonsForRender) {
      const rings = Array.isArray(poly?.paths) ? poly.paths : [];
      for (const ring of rings) {
        for (const pt of Array.isArray(ring) ? ring : []) {
          if (typeof pt?.lat !== "number" || typeof pt?.lng !== "number") continue;
          bounds.extend({ lat: pt.lat, lng: pt.lng });
          added++;
        }
      }
    }
    for (const poly of Array.isArray(hazardPolygons) ? hazardPolygons : []) {
      const rings = Array.isArray(poly?.paths) ? poly.paths : [];
      for (const ring of rings) {
        for (const pt of Array.isArray(ring) ? ring : []) {
          if (typeof pt?.lat !== "number" || typeof pt?.lng !== "number") continue;
          bounds.extend({ lat: pt.lat, lng: pt.lng });
          added++;
        }
      }
    }

    if (!added) return false;

    window.google.maps.event.trigger(map, "resize");
    map.fitBounds(bounds);

    // clamp zoom
    setTimeout(() => {
      const z = map.getZoom?.();
      if (typeof z === "number" && z > 18) map.setZoom(18);
    }, 0);
    return true;
  }, [activeLayer, links, allNodesForRender, parcelsForFit, polygonsForRender, hazardPolygons]);

  const onMapIdle = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const zoom = Number(map.getZoom?.());
    const center = map.getCenter?.();
    const lat = Number(center?.lat?.());
    const lng = Number(center?.lng?.());
    if (Number.isFinite(zoom)) {
      setMapZoom((prev) => (prev === zoom ? prev : zoom));
    }
    if (Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(zoom)) {
      onViewportChange?.({ center: { lat, lng }, zoom });
    }

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => {
      updateViewportBounds();
      idleTimerRef.current = null;
    }, VIEWPORT_IDLE_DEBOUNCE_MS);
  }, [onViewportChange, updateViewportBounds]);

  useEffect(
    () => () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (activeLayer !== "links" && activeLayer !== "nodes" && activeLayer !== "parcels") return;
    if (!isLoaded) return;
    const zoom = Number(mapRef.current?.getZoom?.());
    if (Number.isFinite(zoom)) setMapZoom(zoom);
    updateViewportBounds();
  }, [activeLayer, isLoaded, links.length, nodes.length, parcels.length, updateViewportBounds]);

  useEffect(() => {
    if (!isLoaded) return;
    if (didInitialFitRef.current) return;
    const didFit = fitToData();
    if (didFit) didInitialFitRef.current = true;
  }, [isLoaded, fitToData]);

  useEffect(() => {
    const map = mapRef.current;
    if (!temporalOverlay) {
      fittedTemporalIdRef.current = "";
      return;
    }
    if (!mapReady || !map || !window.google?.maps || !temporalOverlay?.presentationId
      || fittedTemporalIdRef.current === temporalOverlay.presentationId) return;
    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;
    temporalOverlay.links.forEach((link) => link.path.forEach((point) => { bounds.extend(point); count += 1; }));
    temporalOverlay.nodes.forEach((node) => { bounds.extend({ lat: node.lat, lng: node.lng }); count += 1; });
    if (!count) return;
    map.fitBounds(bounds);
    fittedTemporalIdRef.current = temporalOverlay.presentationId;
  }, [mapReady, temporalOverlay]);

  useEffect(() => {
    const map = mapRef.current;
    const googleMaps = window.google?.maps;
    if (!map || !googleMaps || !focusTarget?.id || !focusTarget?.layer || handledFocusTargetRef.current === focusTarget) return;

    const id = String(focusTarget.id);

    if (focusTarget.layer === "links") {
      const link = (links || []).find((l) => String(l?.link_id) === id);
      if (!link?.path?.length) return;

      handledFocusTargetRef.current = focusTarget;
      const bounds = new googleMaps.LatLngBounds();
      link.path.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds);
      setTimeout(() => {
        const z = map.getZoom?.();
        if (typeof z === "number" && z > 18) map.setZoom(18);
      }, 0);
      return;
    }

    if (focusTarget.layer === "nodes") {
      const node = (nodes || []).find((n) => String(n?.node_id) === id);
      if (!node) return;
      handledFocusTargetRef.current = focusTarget;
      map.panTo({ lat: node.lat, lng: node.lng });
      map.setZoom(17);
      return;
    }

    if (focusTarget.layer === "parcels") {
      const parcel = (parcels || []).find((p) => String(p?.parcel_id) === id);
      if (!parcel) return;
      handledFocusTargetRef.current = focusTarget;
      map.panTo({ lat: parcel.lat, lng: parcel.lng });
      map.setZoom(18);
      return;
    }

    if (focusTarget.layer === "polygon") {
      const poly = (polygons || []).find((p) => String(p?.polygon_id) === id);
      const rings = Array.isArray(poly?.paths) ? poly.paths : [];
      if (!rings.length) return;
      const bounds = new googleMaps.LatLngBounds();
      let added = 0;
      rings.forEach((ring) => {
        (Array.isArray(ring) ? ring : []).forEach((pt) => {
          if (typeof pt?.lat !== "number" || typeof pt?.lng !== "number") return;
          bounds.extend({ lat: pt.lat, lng: pt.lng });
          added += 1;
        });
      });
      if (!added) return;
      handledFocusTargetRef.current = focusTarget;
      map.fitBounds(bounds);
      setTimeout(() => {
        const z = map.getZoom?.();
        if (typeof z === "number" && z > 18) map.setZoom(18);
      }, 0);
      return;
    }

    if (focusTarget.layer === "hazard") {
      const rows = id === "__all__"
        ? (hazardPolygons || [])
        : (hazardPolygons || []).filter((row) => String(row?.feature_index) === id);
      const bounds = new googleMaps.LatLngBounds();
      let added = 0;
      rows.forEach((row) => (row?.paths || []).forEach((ring) =>
        (Array.isArray(ring) ? ring : []).forEach((point) => {
          if (!Number.isFinite(Number(point?.lat)) || !Number.isFinite(Number(point?.lng))) return;
          bounds.extend({ lat: Number(point.lat), lng: Number(point.lng) });
          added += 1;
        })
      ));
      if (!added) return;
      handledFocusTargetRef.current = focusTarget;
      map.fitBounds(bounds);
      setTimeout(() => {
        const zoom = map.getZoom?.();
        if (typeof zoom === "number" && zoom > 15) map.setZoom(15);
      }, 0);
    }
  }, [focusTarget, links, nodes, parcels, polygons, hazardPolygons]);

  useEffect(() => {
    const map = mapRef.current;
    const googleMaps = window.google?.maps;
    const type = String(fitSelection?.type || "");
    const ids = new Set((fitSelection?.ids || []).map(String));
    const wholeLayer = fitSelection?.wholeLayer === true;
    if (!map || !googleMaps || (!wholeLayer && !ids.size)) return;

    const bounds = new googleMaps.LatLngBounds();
    let added = 0;
    if (type === "links") {
      (links || []).forEach((link) => {
        if (!wholeLayer && !ids.has(String(link?.link_id))) return;
        (link?.path || []).forEach((point) => {
          bounds.extend(point);
          added += 1;
        });
      });
    } else if (type === "nodes") {
      (nodes || []).forEach((node) => {
        if (!wholeLayer && !ids.has(String(node?.node_id))) return;
        if (!Number.isFinite(Number(node?.lat)) || !Number.isFinite(Number(node?.lng))) return;
        bounds.extend({ lat: Number(node.lat), lng: Number(node.lng) });
        added += 1;
      });
    } else if (type === "parcels") {
      (parcels || []).forEach((parcel) => {
        if (!wholeLayer && !ids.has(String(parcel?.parcel_id))) return;
        if (!Number.isFinite(Number(parcel?.lat)) || !Number.isFinite(Number(parcel?.lng))) return;
        bounds.extend({ lat: Number(parcel.lat), lng: Number(parcel.lng) });
        added += 1;
      });
    }
    if (!added) return;
    map.fitBounds(bounds);
    setTimeout(() => {
      const zoom = map.getZoom?.();
      if (typeof zoom === "number" && zoom > 18) map.setZoom(18);
    }, 0);
  }, [fitSelection, links, nodes, parcels]);

  const baseLinkShapes = useMemo(() => activeLayer === "links" && linksForRender.map((l, idx) => {
    const id = String(l.link_id);
    const isSel = selectedLinkIds?.has?.(id);
    const analysisColor = analysisColorFor(id);
    const selectedDirectionArrow = isSel && (!blockedRoadMode || !selectedBlockedBothDirections.has(id)) && window.google?.maps
      ? [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, fillColor: blockedRoadMode || contraflowMode ? "#ffffff" : "#14532d", fillOpacity: 1, strokeColor: blockedRoadMode || contraflowMode ? "#ffffff" : "#14532d", strokeWeight: 1, scale: 4 }, offset: "50%" }]
      : undefined;
    return <React.Fragment key={`${layerRenderKey}-link-${id}-${idx}`}>
      {isSel && analysisColor && <Polyline
        path={l.path}
        onUnmount={(polyline) => {
          // Hide before detaching so an old analysis outline cannot remain painted.
          polyline.setVisible(false);
          polyline.setMap(null);
        }}
        options={{ strokeColor: blockedRoadMode ? "#dc2626" : contraflowMode ? "#006bff" : "#fde047", strokeOpacity: 1, strokeWeight: 12, clickable: false, zIndex: 4 }}
      />}
      <Polyline path={l.path} onClick={(event) => {
        if (isDrawingDraftLink) return;
        event?.domEvent?.stopPropagation?.();
        onToggleLink?.(id);
      }} options={{
        strokeColor: blockedRoadMode && isSel ? "#dc2626" : analysisColor || (isSel ? (contraflowMode ? "#006bff" : "#BFFFBF") : "#64748b"),
        strokeOpacity: isDrawingDraftLink ? 0.38 : (analysisColor || isSel ? 1 : 0.65),
        strokeWeight: isDrawingDraftLink ? 2 : (analysisColor ? (isSel ? 10 : 6) : (isSel ? 10 : 4)),
        clickable: !isDrawingDraftLink,
        icons: selectedDirectionArrow,
        zIndex: isSel ? 5 : analysisColor ? 3 : 1,
      }} />
    </React.Fragment>;
  }), [activeLayer, linksForRender, selectedLinkIds, analysisColorFor, blockedRoadMode, selectedBlockedBothDirections, contraflowMode, layerRenderKey, isDrawingDraftLink, onToggleLink, isLoaded]);

  if (loadError) {
    return (
      <div style={{ ...containerStyle, padding: 12, color: "tomato" }}>
        Google Maps failed to load: {String(loadError)}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9ca3af",
        }}
      >
        Loading map…
      </div>
    );
  }

  return (
    <div style={{ position: "relative", display: isVisible ? undefined : "none" }}>
      {/* Top-left controls */}
      <div
        style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            gap: 10,
        }}
        >
             <div style={{ display: "flex", gap: 10 }}>
                <input
                    value={searchValue}
                    onChange={(e) => onChangeSearchValue?.(e.target.value)}
                    placeholder="Search address…"
                    style={{
                    width: 460,
                    padding: "8px 10px",
                    borderRadius: 6,
                    border: "1px solid #444",
                    background: "#1f1f1f",
                    color: "#fff",
                    outline: "none",
                    }}
                />
                <button
                    onClick={onSubmitSearch}
                    style={{ ...mapButtonStyle, background: "#2563EB" }}
                >
                    Search
                </button>
            </div>
            
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {hasDisplayLinks && <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", border: "1px solid #374151", borderRadius: 6, background: "rgba(17, 24, 39, 0.92)", color: "#f9fafb", fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={showAllLinks} onChange={(event) => setShowAllLinks(event.target.checked)} />
              Show all links
            </label>}
            {!isSelectingPolygon ? (
            <button
                onClick={onStartPolygon}
                style={{ ...mapButtonStyle, background: "#2563EB" }}
                title="Click points on map to create a polygon"
            >
                Select polygon
            </button>
            ) : (
            <>
                <button
                onClick={onUndoPolygonPoint}
                style={{ ...mapButtonStyle, background: "#374151" }}
                disabled={draftPolygon.length === 0}
                title="Remove last point"
                >
                Undo
                </button>

                <button
                onClick={() => {
                    if (!(isSelectingPolygon && draftPolygon.length >= 3)) return;
                    onSavePolygon?.();
                }}
                style={{
                    ...mapButtonStyle,
                    background: draftPolygon.length >= 3 ? "#16a34a" : "#14532d",
                    opacity: draftPolygon.length >= 3 ? 1 : 0.6,
                }}
                disabled={draftPolygon.length < 3}
                >
                Save polygon
                </button>

                <button onClick={onCancelPolygon} style={{ ...mapButtonStyle, background: "#7f1d1d" }}>
                Cancel
                </button>
            </>
            )}
            {returnToSimulationResults && <button
              type="button"
              onClick={onReturnToSimulationResults}
              style={{ ...mapButtonStyle, background: "#075985", border: "2px solid #7dd3fc", boxShadow: "0 4px 14px rgba(0, 0, 0, .45)", fontWeight: 700 }}
            >
              ← Return to simulation results
            </button>}
            {temporalHazardAvailable && <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", border: "1px solid #374151", borderRadius: 6, background: "rgba(17, 24, 39, 0.92)", color: "#f9fafb", fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={temporalHazardEnabled} onChange={(event) => onTemporalHazardChange?.(event.target.checked)} />
              Hazard layer
            </label>}
        </div>

        </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={defaultZoom}
        onLoad={(map) => {
          mapRef.current = map;
          setMapReady(true);
          if (window.google?.maps) {
            setTimeout(() => window.google.maps.event.trigger(map, "resize"), 0);
          }
          const zoom = Number(map.getZoom?.());
          if (Number.isFinite(zoom)) setMapZoom(zoom);
          setTimeout(() => updateViewportBounds(), 0);
        }}
        onIdle={onMapIdle}
        onClick={onMapClick}
        options={{
          clickableIcons: false,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          gestureHandling: "cooperative",
        }}
      >
        {/* Display-only links used as context while selecting destination nodes. */}
        {overlayLinksForRender.map((l, idx) => (
          <Polyline
            key={`overlay-link-${String(l?.link_id || idx)}-${idx}`}
            path={l.path}
            options={{
              strokeColor: "#64748b",
              strokeOpacity: 0.65,
              strokeWeight: 3,
              clickable: false,
              zIndex: 0,
            }}
          />
        ))}

        {/* LINKS */}
        {baseLinkShapes}

        {temporalHazardPolygons.map((polygon) => <Polygon
          key={`temporal-hazard-${polygon.id}`}
          paths={polygon.paths}
          options={{ fillColor: polygon.layerIndex === 0 ? "#991b1b" : "#6b2100", fillOpacity: polygon.layerIndex === 0 ? 0.3 : 0.62, strokeOpacity: 0, strokeWeight: 0, clickable: false, zIndex: 20 + Math.min(polygon.layerIndex, 10) }}
        />)}

        {/* Timed path sits above the currently selected Builder layer. */}
        {(temporalOverlay?.links || []).map((link) => <TemporalLink key={`temporal-link-${link.id}`} link={link} active={activeTemporalLinks.has(link.id)} color={activeTemporalLinks.get(link.id)?.color} onSelect={onTemporalSelect} />)}
        {(temporalOverlay?.nodes || []).map((node) => <TemporalNode key={`temporal-node-${node.id}`} node={node} active={activeTemporalNodes.has(node.id)} color={activeTemporalNodes.get(node.id)} onSelect={onTemporalSelect} />)}

        {isDrawingDraftLink && draftLinkPath.length >= 1 && (
          <>
            <Polyline
              path={draftLinkPath}
              options={{
                strokeColor: "#f59e0b",
                strokeOpacity: 1,
                strokeWeight: 5,
                clickable: false,
                zIndex: 20,
              }}
            />
            {draftLinkPath.map((pt, idx) => (
              <Circle
                key={`draft-link-pt-${idx}`}
                center={pt}
                radius={idx === 0 || idx === draftLinkPath.length - 1 ? 9 : 6}
                options={{
                  fillColor: idx === 0 ? "#22c55e" : idx === draftLinkPath.length - 1 ? "#ef4444" : "#f59e0b",
                  fillOpacity: 0.95,
                  strokeColor: "#111827",
                  strokeOpacity: 1,
                  strokeWeight: 1,
                  clickable: false,
                  zIndex: 21,
                }}
              />
            ))}
          </>
        )}

        {!!draftLinkFocus?.linkId && (() => {
          const row = (links || []).find((l) => String(l?.link_id) === String(draftLinkFocus.linkId));
          if (!row?.path?.length) return null;
          const start = row.path[0];
          const end = row.path[row.path.length - 1];
          const active = draftLinkFocus.endpoint;
          return (
            <>
              <Circle
                key={`topology-start-${draftLinkFocus.linkId}`}
                center={start}
                radius={active === "start" ? 22 : 12}
                options={{
                  fillColor: "#22c55e",
                  fillOpacity: active === "start" ? 0.95 : 0.75,
                  strokeColor: "#111827",
                  strokeOpacity: 1,
                  strokeWeight: active === "start" ? 3 : 2,
                  clickable: false,
                  zIndex: active === "start" ? 25 : 23,
                }}
              />
              <Circle
                key={`topology-end-${draftLinkFocus.linkId}`}
                center={end}
                radius={active === "end" ? 22 : 12}
                options={{
                  fillColor: "#ef4444",
                  fillOpacity: active === "end" ? 0.95 : 0.75,
                  strokeColor: "#111827",
                  strokeOpacity: 1,
                  strokeWeight: active === "end" ? 3 : 2,
                  clickable: false,
                  zIndex: active === "end" ? 25 : 23,
                }}
              />
            </>
          );
        })()}

        {(draftLinkCandidateArrows || []).map((a) => (
          <Polyline
            key={`draft-candidate-arrow-${a.id}`}
            path={a.path}
            options={{
              strokeColor: a.selected ? "#f59e0b" : "#9ca3af",
              strokeOpacity: 0.95,
              strokeWeight: a.selected ? 4 : 2,
              clickable: false,
              zIndex: a.selected ? 30 : 24,
              icons: window.google?.maps
                ? [{
                    icon: {
                      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                      fillColor: a.selected ? "#f59e0b" : "#9ca3af",
                      fillOpacity: 1,
                      strokeColor: a.selected ? "#f59e0b" : "#9ca3af",
                      scale: a.selected ? 4 : 3,
                    },
                    offset: "100%",
                  }]
                : undefined,
            }}
          />
        ))}

        {/* NODES */}
        {(activeLayer === "nodes" || showNodesOverlay) && nodesForRender.map((n, idx) => {
            const id = String(n.node_id);
            const isSel = selectedNodeIds?.has?.(id);
            const overlayColor = analysisColorFor(id);
            const analysisColor = overlayColor;
            const showNodeHalo = !!isSel && !analysisOverlayActive;

            return (
              <React.Fragment key={`${layerRenderKey}-${showNodesOverlay ? "overlay" : "node"}-${id}-${idx}`}>
                {/* Always mounted and hidden when unused, because mounting it on a condition left
                    the drawn circle behind on the map once the condition turned false. A result
                    keeps the analysis colour, so selection is shown by its larger radius. */}
                <Circle
                  center={{ lat: n.lat, lng: n.lng }}
                  radius={selectedNodeHaloRadius}
                  options={{
                    fillColor: "#facc15",
                    fillOpacity: showNodeHalo ? 0.12 : 0,
                    strokeColor: "#fde047",
                    strokeOpacity: showNodeHalo ? 1 : 0,
                    strokeWeight: 5,
                    clickable: false,
                    visible: showNodeHalo,
                    zIndex: showNodesOverlay ? 9 : 6,
                  }}
                />
                <Circle
                center={{ lat: n.lat, lng: n.lng }}
                radius={showNodesOverlay ? (isSel ? 16 : 8) : (isSel ? 36 : 16)}
                onClick={(e) => {
                    // prevent polygon map-click from also adding a point when clicking a node
                    e?.domEvent?.stopPropagation?.();
                    onToggleNode?.(id);
                }}
                options={{
                    fillColor: analysisColor
                      || (isSel ? (contraflowMode ? "#facc15" : "#BFFFBF") : (analysisOverlayActive ? "#94a3b8" : (showNodesOverlay ? "#06B6D4" : "#1E90FF"))),
                    fillOpacity: isSel ? 1 : (showNodesOverlay ? 0.85 : 0.75),
                    strokeColor: "#000",
                    strokeOpacity: 1,
                    strokeWeight: isSel ? 2 : (showNodesOverlay ? 1.5 : 1),
                    clickable: true,
                    zIndex: isSel ? 12 : showNodesOverlay ? 8 : 1,
                }}
                />
              </React.Fragment>
            );
        })}

        {/* PARCELS */}
        {activeLayer === "parcels" && parcelsForRender.map((p, idx) => {
          const id = String(p.parcel_id);
          const isSel = selectedParcelIds?.has?.(id);
          const analysisColor = analysisColorFor(id);
          return (
          <Circle
            key={`${layerRenderKey}-parcel-${id}-${idx}`}
            center={{ lat: p.lat, lng: p.lng }}
            radius={isSel ? 16 : 6}
            onClick={(e) => {
              e?.domEvent?.stopPropagation?.();
              onToggleParcel?.(id);
            }}
            options={{
              fillColor: analysisColor || (isSel ? "#BFFFBF" : "#00BFFF"),
              fillOpacity: isSel ? 1 : 0.7,
              strokeColor: "#000",
              strokeOpacity: 1,
              strokeWeight: isSel ? 2 : 1,
              clickable: true,
              zIndex: isSel ? 5 : 1,
            }}
          />
          );
        })}

        {/* POLYGON LAYERS */}
        {(activeLayer === "polygon" || showPolygonOverlay) &&
          (polygonsForRender || []).map((poly, idx) => {
            const id = String(poly?.polygon_id || "");
            const paths = Array.isArray(poly?.paths) ? poly.paths : [];
            if (!id || !paths.length) return null;
            const isSel = selectedPolygonIds?.has?.(id);
            return (
              <Polygon
                key={`${layerRenderKey}-${showPolygonOverlay ? "overlay-poly" : "layer-poly"}-${id}-${idx}`}
                paths={paths}
                onClick={() => {
                  if (showPolygonOverlay) return;
                  onTogglePolygon?.(id);
                }}
                options={{
                  fillColor: showPolygonOverlay ? "#f59e0b" : isSel ? selectedPolygonColor : "#22d3ee",
                  fillOpacity: showPolygonOverlay ? 0.12 : isSel ? 0.42 : 0.2,
                  strokeColor: showPolygonOverlay ? "#f97316" : isSel ? selectedPolygonColor : "#06b6d4",
                  strokeOpacity: 0.95,
                  strokeWeight: showPolygonOverlay ? 3 : isSel ? 4 : 2,
                  clickable: !showPolygonOverlay,
                  zIndex: showPolygonOverlay ? 10 : isSel ? 8 : 4,
                }}
              />
            );
          })}

        {/* Temporal hazard snapshot (display-only). */}
        {(hazardPolygons || []).map((poly, idx) => {
          const paths = Array.isArray(poly?.paths) ? poly.paths : [];
          if (!paths.length) return null;
          const isSelected = String(poly?.feature_index) === String(selectedHazardFeatureIndex);
          return (
            <Polygon
              key={`hazard-${String(poly?.polygon_id || idx)}-${idx}`}
              paths={paths}
              onClick={() => onSelectHazardFeature?.(poly?.feature_index)}
              options={{
                fillColor: isSelected ? "#facc15" : "#ef4444",
                fillOpacity: isSelected ? 0.48 : 0.28,
                strokeColor: isSelected ? "#fde047" : "#f97316",
                strokeOpacity: 1,
                strokeWeight: isSelected ? 5 : 3,
                clickable: true,
                zIndex: isSelected ? 18 : 15,
              }}
            />
          );
        })}

        {/* POLYGON overlay */}
        {polygonToRender && polygonToRender.length >= 2 && (
          <>
            <Polygon
              paths={polygonToRender}
              options={{
                fillColor: "#FFFF00",
                fillOpacity: 0.2,
                strokeColor: "#FFFF00",
                strokeOpacity: 0.9,
                strokeWeight: 2,
                clickable: false,
                zIndex: 5,
              }}
            />
            {polygonToRender.map((pt, idx) => (
              <Circle
                key={`poly-vtx-${idx}`}
                center={pt}
                radius={4}
                options={{
                  fillColor: "#FFFF00",
                  fillOpacity: 0.95,
                  strokeColor: "#FFFF00",
                  strokeOpacity: 0.95,
                  strokeWeight: 1,
                  clickable: false,
                  zIndex: 6,
                }}
              />
            ))}
          </>
        )}
      </GoogleMap>
      {analysisOverlayActive && <div className="mb-analysisLegend">
        <strong>{analysisOverlay.metricLabel}</strong>
        {analysisOverlay.ranges.map((range, index) => <span key={index}><i style={{ background: range.color }} />{Number(range.min).toLocaleString()}–{Number(range.max).toLocaleString()}</span>)}
      </div>}
      {Number.isFinite(mapZoom) && mapZoom < MIN_DETAIL_RENDER_ZOOM &&
        ((hasDisplayLinks && !showAllLinks) || activeLayer === "nodes") && (
        <div
          style={{
            position: "absolute",
            right: 12,
            top: 12,
            zIndex: 10,
            background: "rgba(17, 24, 39, 0.9)",
            color: "#d1d5db",
            border: "1px solid #374151",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12,
          }}
        >
          {hasDisplayLinks && !showAllLinks ? "Zoom in to view links" : "Zoom in to view nodes"}
        </div>
      )}
    </div>
  );
}
